import { useCallback, useMemo, useRef, useState } from 'react';

import { SparklesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { PAGE_GUIDELINES_MAX } from '@/shared/types/teaser';

import { splitPageUrl, titleFromPath } from './page-url';
import { TeaserPageForm, TeaserSectionForm, emptyTeaserPage } from './types';
import { useGenerateSections } from './useGenerateSections';

type Props = {
  /**
   * The site every existing row hangs off, or `''` when this is the first page.
   * A second page on another origin is refused against this.
   */
  websiteUrl: string;
  /** Host to prefill, so nobody types their own domain. Without a scheme. */
  suggestedHost: string;
  deskId?: string;
  integrationId?: string;
  /** Hands back the split address and the finished row, for the caller to store. */
  onAdd: (origin: string, page: TeaserPageForm) => void;
  onCancel: () => void;
};

/**
 * A bot wall is the one generate failure the author can act on. `fetcher` spreads
 * the error payload onto the Error, so `code` is there.
 */
const generateErrorKey = (error: unknown): string =>
  (error as { code?: string })?.code === 'waf_blocked'
    ? 'integrations.web_page_preview.errors.waf_blocked'
    : 'integrations.web_teaser.pages.generate_error';

/** `https://moveo.ai` → `moveo.ai`, because the scheme is the field's addon. */
const hostOf = (origin: string): string => origin.trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');

/**
 * Adding a webpage, which is also the only way to generate its quick actions.
 *
 * Generation is not a button beside an existing row — it is how a row comes
 * into being. The author gives an address and, optionally, what they want said;
 * what comes back *is* the page. Nothing here is a form field until it lands:
 * the draft is local state, so cancelling leaves no half-built row in
 * `teaser.pages` for the serialiser to have an opinion about.
 */
const AddWebpageCard = ({
  websiteUrl,
  suggestedHost,
  deskId,
  integrationId,
  onAdd,
  onCancel,
}: Props) => {
  const { t } = useTranslation();

  const [value, setValue] = useState(() => hostOf(websiteUrl || suggestedHost));
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  /**
   * The split, and the one rule that cannot be expressed by splitting alone:
   * `website_url` is a single block-level field, so every row has to agree on
   * an origin. Refusing the second origin is the safe direction — overwriting
   * it would silently repoint every existing row's preview at another site.
   */
  const resolve = useCallback((): { origin: string; path: string } | null => {
    const split = splitPageUrl(value);
    if (!split) {
      setError(t('integrations.web_teaser.pages.url_invalid'));
      return null;
    }
    if (websiteUrl && split.origin !== websiteUrl) {
      setError(t('integrations.web_teaser.pages.different_origin', { 0: websiteUrl }));
      return null;
    }
    setError(null);
    return split;
  }, [t, value, websiteUrl]);

  const buildPage = useCallback(
    (path: string, sections: TeaserSectionForm[]): TeaserPageForm => ({
      ...emptyTeaserPage(),
      url: path,
      title: titleFromPath(path) || hostOf(value),
      guidelines: prompt.trim(),
      // The page itself says nothing — it is a container for its sections. See
      // the note in `toTeaserPage`.
      variant: 'none',
      sections,
    }),
    [prompt, value]
  );

  /**
   * What the in-flight request was split from.
   *
   * A ref, not state: the success handler needs the exact address the request
   * was made for, and re-splitting the field on the way out would make the
   * result quietly depend on the field not having changed underneath it.
   */
  const pendingRef = useRef<{ origin: string; path: string } | null>(null);

  const {
    generate,
    isPending,
    error: generateError,
  } = useGenerateSections(deskId, integrationId, {
    onGenerated: (sections) => {
      const pending = pendingRef.current;
      if (!pending) {
        return;
      }
      onAdd(pending.origin, buildPage(pending.path, sections));
    },
    onEmpty: () => setIsEmpty(true),
  });

  const handleGenerate = useCallback(() => {
    const split = resolve();
    if (!split) {
      return;
    }
    setIsEmpty(false);
    pendingRef.current = split;
    generate({ url: `${split.origin}${split.path}`, guidelines: prompt });
  }, [generate, prompt, resolve]);

  const addon = useMemo(() => <InputGroupAddon>https://</InputGroupAddon>, []);

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-stroke bg-bg-muted-light p-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="teaser-add-webpage" className="text-sm leading-5 font-medium">
          {t('integrations.web_teaser.pages.webpage')}
        </Label>
        <InputGroup>
          {addon}
          <InputGroupInput
            id="teaser-add-webpage"
            autoFocus
            placeholder={t('integrations.web_teaser.pages.webpage_placeholder')}
            aria-invalid={!!error}
            disabled={isPending}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              // Enter here must not reach the accordion's form: this card is
              // not part of it, and submitting would save the integration.
              if (event.key === 'Enter') {
                event.preventDefault();
                handleGenerate();
              }
            }}
          />
        </InputGroup>
        {error && <p className="text-sm leading-5 text-warning">{error}</p>}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="teaser-add-webpage-prompt" className="text-sm leading-5 font-medium">
          {t('integrations.web_teaser.pages.prompt')}
        </Label>
        {/*
            Instructions for the model, not copy for the visitor. Stored on the
            page as `guidelines` so it is still there if the page is ever
            regenerated.
          */}
        <Textarea
          id="teaser-add-webpage-prompt"
          rows={4}
          maxLength={PAGE_GUIDELINES_MAX}
          placeholder={t('integrations.web_teaser.pages.prompt_placeholder')}
          disabled={isPending}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
        />
      </div>

      {isEmpty && (
        <Alert variant="info">
          <AlertDescription>{t('integrations.web_teaser.pages.generate_empty')}</AlertDescription>
        </Alert>
      )}

      {generateError && (
        <Alert variant="warning">
          <AlertDescription>{t(generateErrorKey(generateError))}</AlertDescription>
        </Alert>
      )}

      {isPending ? (
        <div className="flex items-center gap-2 text-sm leading-5 text-foreground-muted">
          <Spinner className="size-4 text-current" />
          {t('integrations.web_teaser.pages.generating')}
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost-neutral" size="sm" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="outline-neutral" size="sm" onClick={handleGenerate}>
            <SparklesIcon size={16} />
            {t('common.generate')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AddWebpageCard;
